import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useAlert,
  useAuth,
  useSessionStorage,
} from '@spotify/confidence-core-react';

import {
  AccountDetailsState,
  DEFAULT_STATE,
  isAccountUnderReviewError,
  isEmailNotVerifiedError,
} from '../signupUtils';
import { getSignupAttribution } from './attribution';
import { useAuthConnections } from './useAuthConnections';
import { CreateAccountRequest, useCreateAccount } from './useCreateAccount';
import { useEmailVerification } from './useEmailVerification';
import type { CreatedAccount } from './useInvite';
import { useSignupTracking } from './useSignupTracking';
import { useWorkspaceNameFields } from './useWorkspaceNameFields';

export type SignupScreen =
  | 'loading'
  | 'verify_email'
  | 'under_review'
  | 'workspace_setup'
  | 'login_verify_email'
  | 'welcome';

export const useCreateAccountForm = ({
  inviteId,
  token,
  onCreate,
}: {
  inviteId?: string;
  token?: string;
  onCreate: (account?: CreatedAccount) => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const alert = useAlert();
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithPopup,
    getAccessTokenSilently,
    error: authError,
  } = useAuth();

  const [{ value: createdAccount, error }, createAccount] =
    useCreateAccount(inviteId);
  const [showOverlay, setShowOverlay] = useState(false);

  const { trackStep, trackCompletion, setOrgContext, getPreviousStepId } =
    useSignupTracking('slim_wizard', { enabled: isAuthenticated });

  useEffect(() => {
    if (authError && !isEmailNotVerifiedError(authError)) {
      alert.post({
        message: `Failed to log in: ${authError.message}`,
        severity: 'error',
      });
    }
  }, [authError, alert]);

  const signupAttribution = useMemo(
    () => getSignupAttribution(searchParams),
    [searchParams],
  );

  const [acceptMarketingEmails, setAcceptMarketingEmails] =
    useState<boolean>(true);
  const [accountDetails, setAccountDetails] =
    useSessionStorage<AccountDetailsState>(
      `signup-slim-v1-${inviteId ?? 'regular'}`,
      DEFAULT_STATE,
    );

  useEffect(() => {
    if (createdAccount) {
      trackCompletion('success');
    }
  }, [createdAccount, trackCompletion]);

  const handleResultDismissed = useCallback(() => {
    setShowOverlay(false);
    if (createdAccount) {
      onCreate(createdAccount);
      setAccountDetails(DEFAULT_STATE);
    }
  }, [createdAccount, onCreate, setAccountDetails]);

  useEffect(() => {
    if (error) {
      trackCompletion('error', {
        error_message: error.message,
        final_step_id: 'workspace_setup',
      });
    }
  }, [error, trackCompletion]);

  const setField = useCallback(
    (newData: Partial<AccountDetailsState>) => {
      setAccountDetails(current => ({ ...current, ...newData }));
    },
    [setAccountDetails],
  );

  useEffect(() => {
    if (!user?.email) return;
    setField({ adminEmail: user.email });
  }, [user?.email, setField]);

  const {
    toggleAuthConnection,
    hasSelectedAuthConnection,
    showAuthConnections,
    userAuthConnection,
  } = useAuthConnections({
    user,
    inviteId,
    authConnections: accountDetails.authConnections,
    setAccountDetails,
  });

  const {
    loginId,
    handleDisplayNameChange,
    handleDisplayNameBlur,
    validDisplayName,
    validLoginId,
    helperText,
    loginIdAvailable,
    loginIdError,
    loginIdConfirmedAvailable,
    isCheckingAvailability,
  } = useWorkspaceNameFields({
    displayName: accountDetails.displayName,
    setField,
    inviteId,
  });

  useEffect(() => {
    setOrgContext({
      display_name: accountDetails.displayName || undefined,
      workspace_name: loginId || undefined,
    });
  }, [accountDetails.displayName, loginId, setOrgContext]);

  useEffect(() => {
    if (!searchParams.has('step')) return;
    searchParams.delete('step');
    setSearchParams(searchParams, { replace: true });
  }, []);

  const isEmailVerified = user?.email_verified ?? false;

  const deriveScreen = (): SignupScreen => {
    if (isLoading) return 'loading';
    if (isAuthenticated && !isEmailVerified) return 'verify_email';
    if (isAuthenticated && isAccountUnderReviewError(error)) {
      return 'under_review';
    }
    if (isAuthenticated) return 'workspace_setup';
    if (isEmailNotVerifiedError(authError)) return 'login_verify_email';
    return 'welcome';
  };
  const screen = deriveScreen();

  const hasTrackedRef = useRef(false);
  useEffect(() => {
    if (screen !== 'workspace_setup' || hasTrackedRef.current) return;
    hasTrackedRef.current = true;
    trackStep('impression', {
      step_id: 'workspace_setup',
      step_name: 'Workspace Setup',
      step_index: 0,
      step_count: 1,
      navigation: {
        entry_mode: 'direct',
        previous_step_id: getPreviousStepId(),
      },
    });
  }, [screen, trackStep, getPreviousStepId]);

  const handleLogin = async () => {
    await loginWithPopup(
      { authorizationParams: { screen_hint: 'signup' } },
      { timeoutInSeconds: 600 },
    );
  };

  const handleCreate = () => {
    if (!user) return;
    setShowOverlay(true);

    createAccount(
      {
        displayName: accountDetails.displayName,
        loginId: loginId,
        region: accountDetails.region,
        authConnections: Object.entries(accountDetails.authConnections).flatMap(
          ([connection, enabled]) => (enabled ? [{ [connection]: {} }] : []),
        ) as CreateAccountRequest['authConnections'],
        adminEmail:
          accountDetails.adminEmail !== ''
            ? accountDetails.adminEmail
            : undefined,
        allowedLoginEmailDomains:
          inviteId && accountDetails.authConnections.googleAuthConnection
            ? [accountDetails.loginDomain].filter(
                (domain): domain is string => !!domain,
              )
            : undefined,
        marketingOptIn: acceptMarketingEmails,
        signupAttribution,
      },
      token,
    );
  };

  const hasTrackedVerifyEmailRef = useRef(false);
  useEffect(() => {
    if (screen !== 'verify_email' || hasTrackedVerifyEmailRef.current) return;
    hasTrackedVerifyEmailRef.current = true;
    trackStep('impression', {
      step_id: 'verify_email',
      step_name: 'Verify Email',
      step_index: -1,
      step_count: 2,
      navigation: {
        entry_mode: 'direct',
        previous_step_id: getPreviousStepId(),
      },
    });
  }, [screen, trackStep, getPreviousStepId]);

  const { recheckEmailVerification } = useEmailVerification({
    getAccessTokenSilently,
    isAuthenticated,
    isEmailVerified,
  });

  const canSubmit =
    !!loginId &&
    loginIdAvailable &&
    validDisplayName &&
    validLoginId &&
    !!accountDetails.displayName &&
    (!showAuthConnections || hasSelectedAuthConnection) &&
    !showOverlay;

  return {
    screen,
    error,
    createdAccount,
    showOverlay,
    canSubmit,

    accountDetails,
    loginId,
    setField,
    acceptMarketingEmails,
    setAcceptMarketingEmails,

    handleDisplayNameChange,
    handleDisplayNameBlur,

    validDisplayName,
    validLoginId,
    helperText,
    loginIdAvailable,
    loginIdError,
    loginIdConfirmedAvailable,
    isCheckingAvailability,

    showAuthConnections,
    hasSelectedAuthConnection,
    userAuthConnection,
    toggleAuthConnection,

    handleLogin,
    handleCreate,
    handleResultDismissed,
    recheckEmailVerification,
  };
};

export type CreateAccountFormState = ReturnType<typeof useCreateAccountForm>;
